import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useId, useState } from 'react';
/**
 * RFC 4122 v4 where the platform provides it, and a non-cryptographic fallback
 * otherwise — the key only has to be unique per intent, not unguessable.
 */
function newIntentKey() {
    const c = globalThis.crypto;
    if (c?.randomUUID)
        return c.randomUUID();
    return `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
import { Button } from './Button.js';
import { Dialog } from './Dialog.js';
import { cn } from '../cn.js';
/**
 * Trigger button that opens a confirmation modal demanding a typed
 * reason before the dangerous action runs. The reason is forwarded to
 * the caller-supplied `onConfirm`, which is responsible for issuing
 * the actual API call (and supplying the audit trail).
 *
 * The trigger uses the `danger` button variant by default; the modal
 * is rendered inside the document flow (no portal) so it composes
 * naturally with the existing detail-page layouts.
 */
export function DangerousActionButton({ actionLabel, title, description, onConfirm, confirmLabel = 'Confirm', minReasonLength = 5, disabled, testId, className, }) {
    const [open, setOpen] = useState(false);
    // One key per open dialog: created when it opens, kept across every retry
    // inside it, replaced only on reopen.
    const [intentKey, setIntentKey] = useState(() => newIntentKey());
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const reasonId = useId();
    const reset = useCallback(() => {
        setReason('');
        setError(null);
        setSubmitting(false);
    }, []);
    const close = useCallback(() => {
        setOpen(false);
        reset();
    }, [reset]);
    // Focus capture, trap, restore and Escape now live in <Dialog>.
    async function onSubmit(e) {
        e.preventDefault();
        const trimmed = reason.trim();
        if (trimmed.length < minReasonLength) {
            setError(`Reason must be at least ${minReasonLength} characters.`);
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            await onConfirm(trimmed, intentKey);
            close();
        }
        catch (err) {
            setError(err instanceof Error && err.message
                ? err.message
                : 'Action failed. Try again or contact an administrator.');
            setSubmitting(false);
        }
    }
    const heading = title ?? actionLabel;
    return (_jsxs(_Fragment, { children: [_jsx(Button, { variant: "danger", onClick: () => {
                    setIntentKey(newIntentKey());
                    setOpen(true);
                }, disabled: disabled, "data-testid": testId ?? 'dangerous-action-trigger', className: className, type: "button", children: actionLabel }), open ? (_jsx(Dialog, { title: heading, onClose: close, 
                // A stray Escape must not discard a typed reason mid-flight.
                dismissible: !submitting, initialFocus: "textarea", "data-testid": `${testId ?? 'dangerous-action'}-dialog`, children: _jsxs(_Fragment, { children: [_jsx("p", { className: "font-body-md text-body-md text-on-surface-variant", children: description }), _jsxs("form", { onSubmit: onSubmit, className: "space-y-3", children: [_jsxs("label", { htmlFor: reasonId, className: "font-label-md text-label-md uppercase text-on-surface-variant block", children: ["Reason (min ", minReasonLength, " characters)"] }), _jsx("textarea", { id: reasonId, value: reason, onChange: (e) => setReason(e.target.value), rows: 3, required: true, minLength: minReasonLength, maxLength: 500, className: cn('w-full px-3 py-2 rounded border border-outline-variant bg-surface text-on-surface', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-error'), "data-testid": `${testId ?? 'dangerous-action'}-reason`, disabled: submitting }), error ? (_jsx("div", { role: "alert", className: "text-error font-body-md text-body-md", "data-testid": `${testId ?? 'dangerous-action'}-error`, children: error })) : null, _jsxs("div", { className: "flex justify-end gap-2 pt-1", children: [_jsx(Button, { variant: "ghost", type: "button", onClick: close, disabled: submitting, children: "Cancel" }), _jsx(Button, { variant: "danger", type: "submit", disabled: submitting || reason.trim().length < minReasonLength, "data-testid": `${testId ?? 'dangerous-action'}-confirm`, children: submitting ? 'Working…' : confirmLabel })] })] })] }) })) : null] }));
}
//# sourceMappingURL=DangerousActionButton.js.map